

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    ButtonGroup,
    Box,
    Alert,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const EMPTY_FORM = {
    name: "",
    description: "",
    type: "vr",
    status: "upcoming",
    startDate: new Date(),
    endDate: new Date(),
    isRecurring: false,
    MaxPlayers: 0,
    RequiredPlayersToStart: 0,
    scenario: undefined,
    pin: null,
};

const EVENT_FIELDS = {
    vr: ["players", "scenario"],
    training: ["limit"],
    meeting: [],
    event: [],
    information: [],
};

const TYPE_LABELS = {
    vr: "Wydarzenie VR",
    training: "Szkolenie",
    meeting: "Spotkanie",
    event: "Wydarzenie",
    information: "Informacja",
};

export default function EventDialog({
    open,
    onClose,
    onSave,
    onDelete,
    event,
    slot,
    scenarioNames,
    alert,
    joinEvent,
    leaveEvent,
    currentUser,
    selectedGroup
}) {
    const isEdit = Boolean(event);
    const isCreator = event?.meta?.creator?.id === currentUser?.user?.id;

    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [selected, setSelected] = useState("vr");

    useEffect(() => {
        if (!open) return;

        if (isEdit && event) {
            setForm({
                name: event.title || "",
                description: event.meta?.description || "",
                scenario: event.meta?.scenario || undefined,
                type: event.meta?.type || "vr",
                status: event.meta?.status || "upcoming",
                startDate: event.start,
                endDate: event.end,
                isRecurring: event.meta?.isRecurring || false,
                MaxPlayers: event.meta?.MaxPlayers ?? 0,
                RequiredPlayersToStart: event.meta?.RequiredPlayersToStart ?? 0,
                pin: event.meta?.pin ?? null,
            });
            setSelected(event.meta?.type || "vr");
        } else if (slot) {
            setForm({ ...EMPTY_FORM, startDate: slot.start, endDate: slot.end });
            setSelected("vr");
        } else {
            setForm({ ...EMPTY_FORM });
            setSelected("vr");
        }
    }, [open, event, slot, isEdit]);

    const handleSelect = (type) => {
        if (!isCreator && isEdit) return;
        setSelected(type);
        setForm(prev => ({
            ...prev,
            type,
            scenario: type === "vr" ? prev.scenario : undefined,
            MaxPlayers: 0,
            RequiredPlayersToStart: 0,
        }));
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        if (!isCreator && isEdit) return;
        setForm(prev => ({
            ...prev,
            [name]: type === "number" ? parseInt(value, 10) || 0 : value,
        }));
    };

    const handleDateChange = (name, value) => {
        if (!isCreator && isEdit) return;
        setForm(prev => ({ ...prev, [name]: new Date(value) }));
    };

    const handleSubmit = () => {
        if (!isCreator && isEdit) return;
        onSave(form);
        onClose();
    };

    const activeFields = EVENT_FIELDS[selected] || [];
    const showPlayers = activeFields.includes("players");
    const showScenario = activeFields.includes("scenario");
    const showLimit = activeFields.includes("limit");

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? "Edytuj wydarzenie" : "Dodaj wydarzenie"}</DialogTitle>

            <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, alignSelf: 'center' }}>
                <ButtonGroup size="small" aria-label="Event type selector">
                    {["vr", "event", "meeting", "training", "information"].map(type => (
                        <Button
                            key={type}
                            variant={selected === type ? "contained" : "outlined"}
                            onClick={() => handleSelect(type)}
                            disabled={isEdit && !isCreator}
                        >
                            {TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                        </Button>
                    ))}
                </ButtonGroup>
            </Box>

            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: "500px",
                    overflowY: "auto",
                }}
            >
                <TextField
                    label="Nazwa wydarzenia"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={isEdit && !isCreator}
                />
                <TextField
                    label="Grupa"
                    name="group"
                    value={selectedGroup}
                    fullWidth
                    required
                    disabled={true}
                />
                <TextField
                    label="Opis"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    disabled={isEdit && !isCreator}
                />

                {showPlayers && (isCreator || !isEdit) && (
                    <>
                        <TextField
                            label="Minimalna liczba graczy"
                            name="RequiredPlayersToStart"
                            value={form.RequiredPlayersToStart}
                            onChange={handleChange}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Maksymalna liczba graczy"
                            name="MaxPlayers"
                            value={form.MaxPlayers}
                            onChange={handleChange}
                            type="number"
                            inputProps={{ min: 0 }}
                        />
                    </>
                )}

                {showLimit && (isCreator || !isEdit) && (
                    <TextField
                        label="Limit uczestników"
                        name="MaxPlayers"
                        value={form.MaxPlayers}
                        onChange={handleChange}
                        type="number"
                        inputProps={{ min: 0 }}
                    />
                )}

                {showScenario && (isCreator || !isEdit) && (
                    <TextField
                        select
                        label="Scenariusz"
                        name="scenario"
                        value={form.scenario}
                        onChange={handleChange}
                    >
                        {scenarioNames
                            ?.filter(
                                s =>
                                    !s.name?.includes("DEMO") &&
                                    !s.name?.includes("LOCKED")
                            )
                            .map(s => (
                                <MenuItem key={s.scenarioName} value={s.scenarioName}>
                                    {s.name}
                                </MenuItem>
                            ))}
                    </TextField>
                )}

                {/* {showScenario && (isCreator || !isEdit) && (
                    <TextField
                        select
                        label="Scenariusz"
                        name="scenario"
                        value={form.scenario}
                        onChange={handleChange}
                    >
                        {scenarioNames?.map(s => (
                            <MenuItem key={s.scenarioName} value={s.scenarioName}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )} */}

                <TextField
                    type="datetime-local"
                    label="Start"
                    InputLabelProps={{ shrink: true }}
                    value={formatDate(form.startDate)}
                    onChange={e => handleDateChange("startDate", e.target.value)}
                    disabled={isEdit && !isCreator}
                />
                <TextField
                    type="datetime-local"
                    label="Koniec"
                    InputLabelProps={{ shrink: true }}
                    value={formatDate(form.endDate)}
                    onChange={e => handleDateChange("endDate", e.target.value)}
                    disabled={isEdit && !isCreator}
                />

                <TextField
                    label="PIN"
                    name="pin"
                    value={form.pin}
                    onChange={handleChange}
                    type="password"
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 4,
                    }}
                />
            </DialogContent>

            {alert && (
                <Box sx={{ p: 2 }}>
                    <Alert variant="outlined" severity={alert.severity}>
                        {alert.message}
                    </Alert>
                </Box>
            )}

            <DialogActions sx={{ justifyContent: "space-between", mt: 2 }}>
                {isEdit && isCreator && <Button color="error" onClick={onDelete}>Usuń</Button>}
                {(!isEdit || isCreator) && (
                    <Button variant="contained" onClick={handleSubmit}>
                        {isEdit ? "Zapisz" : "Dodaj"}
                    </Button>
                )}

                {!isCreator && isEdit && event && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {!event?.meta?.participants?.some(p => p.id === currentUser.user.id) ? (
                            <Button
                                variant="contained"
                                onClick={() => joinEvent(event.id)}
                                sx={{ backgroundColor: "#4caf50", color: "white" }}
                            >
                                Dołącz
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => leaveEvent(event.id)}
                                sx={{ backgroundColor: "#f44336", color: "white" }}
                            >
                                Opuść
                            </Button>
                        )}
                    </Box>
                )}

                <Button onClick={onClose}>Anuluj</Button>
            </DialogActions>
        </Dialog>
    );
}

const formatDate = (date) => new Date(date).toISOString().slice(0, 16);






// import {
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     Button,
//     MenuItem,
//     Checkbox,
//     FormControlLabel,
//     ButtonGroup,
//     Box,
//     Alert,
// } from "@mui/material";
// import { useEffect, useState } from "react";


// const EMPTY_FORM = {
//     name: "",
//     description: "",
//     type: "special",
//     status: "upcoming",
//     startDate: new Date(),
//     endDate: new Date(),
//     isRecurring: false,
// };

// const EVENT_FIELDS = {
//     vr: ["players", "scenario"],
//     training: ["limit"],
//     meeting: [],
//     event: [],
//     information: [],
// };


// export default function EventDialog({
//     open,
//     onClose,
//     onSave,
//     onDelete,
//     event,
//     slot,
//     scenarioNames,
//     alert,
//     joinEvent,
//     leaveEvent,
//     currentUser,
// }) {
//     const isEdit = Boolean(event);

//     const [form, setForm] = useState({
//         creatorId: 1,
//         name: "",
//         description: "",
//         scenario: undefined,
//         type: "special",
//         status: "upcoming",
//         startDate: new Date(),
//         endDate: new Date(),
//         isRecurring: false,
//         MaxPlayers: 0,
//         RequiredPlayersToStart: 0,
//     });

//     const [selected, setSelected] = useState("vr");

//     const handleSelect = (type) => {
//         setSelected(type);

//         setForm(prev => ({
//             ...prev,
//             type: type,
//             isRecurring: false,
//             MaxPlayers: 0,
//             RequiredPlayersToStart: 0,
//             scenario: type === "vr" ? prev.scenario || undefined : undefined,
//         }));
//     };

//     console.log('form', form)

//     console.log('eventggggg', event)

//     console.log('currentUser', currentUser)

//     useEffect(() => {
//         if (!open) return;
//         if (event) {
//             console.log('event.meta', event?.meta)
//             // EDIT
//             setForm({
//                 name: event.title || "",
//                 description: event.meta?.description || "",
//                 scenario: event.meta?.scenario || undefined,
//                 type: event.meta?.type || "special",
//                 status: event.meta?.status || "upcoming",
//                 startDate: event.start,
//                 endDate: event.end,
//                 isRecurring: event.meta?.isRecurring || false,
//                 MaxPlayers: event.meta?.MaxPlayers != null ? parseInt(event.meta.MaxPlayers, 10) : 0,
//                 RequiredPlayersToStart: event.meta?.RequiredPlayersToStart != null
//                     ? parseInt(event.meta.RequiredPlayersToStart, 10)
//                     : 0,
//             });
//             setSelected(event.meta?.type || "vr");
//         } else if (slot) {
//             setForm({
//                 ...EMPTY_FORM,
//                 startDate: slot.start,
//                 endDate: slot.end,
//             });
//         } else {
//             setForm(EMPTY_FORM);
//         }
//     }, [open, event, slot]);


//     const handleChange = (e) => {
//         const { name, value, type, checked } = e.target;
//         setForm((prev) => ({
//             ...prev,
//             [name]: type === "number" ? (value === "" ? 0 : parseInt(value, 10)) : value,
//         }));
//     };

//     const handleDateChange = (name, value) => {
//         setForm((prev) => ({
//             ...prev,
//             [name]: new Date(value),
//         }));
//     };

//     const handleSubmit = () => {
//         onSave(form);
//         onClose();
//     };
//     const activeFields = EVENT_FIELDS[selected] || [];

//     const showPlayers = activeFields.includes("players");
//     const showScenario = activeFields.includes("scenario");
//     const showLimit = activeFields.includes("limit");

//     return (
//         <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
//             <DialogTitle>
//                 {isEdit ? "Edytuj wydarzenie" : "Dodaj wydarzenie"}
//             </DialogTitle>

//             <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, alignSelf: 'center' }}>
//                 <ButtonGroup size="small" aria-label="Event type selector">
//                     <Button
//                         variant={selected === "vr" ? "contained" : "outlined"}
//                         onClick={() => handleSelect("vr")}
//                     >
//                         Wydarzenie VR
//                     </Button>

//                     <Button
//                         variant={selected === "event" ? "contained" : "outlined"}
//                         onClick={() => handleSelect("event")}
//                     >
//                         Wydarzenie
//                     </Button>

//                     <Button
//                         variant={selected === "meeting" ? "contained" : "outlined"}
//                         onClick={() => handleSelect("meeting")}
//                     >
//                         Spotkanie
//                     </Button>

//                     <Button
//                         variant={selected === "training" ? "contained" : "outlined"}
//                         onClick={() => handleSelect("training")}
//                     >
//                         Szkolenie
//                     </Button>

//                     <Button
//                         variant={selected === "information" ? "contained" : "outlined"}
//                         onClick={() => handleSelect("information")}
//                     >
//                         Informacja
//                     </Button>
//                 </ButtonGroup>
//             </Box>

//             <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

//                 <TextField
//                     label="Nazwa wydarzenia"
//                     name="name"
//                     value={form.name}
//                     onChange={handleChange}
//                     fullWidth
//                     required
//                 />

//                 <TextField
//                     label="Opis"
//                     name="description"
//                     value={form.description}
//                     onChange={handleChange}
//                     multiline
//                     rows={3}
//                 />

//                 {showPlayers && (
//                     <>
//                         <TextField
//                             label="Minimalna liczba graczy"
//                             name="RequiredPlayersToStart"
//                             value={form.RequiredPlayersToStart ?? ""}
//                             onChange={handleChange}
//                             type="number"
//                             inputProps={{ min: 0 }}
//                         />

//                         <TextField
//                             label="Maksymalna liczba graczy"
//                             name="MaxPlayers"
//                             value={form.MaxPlayers ?? ""}
//                             onChange={handleChange}
//                             type="number"
//                             inputProps={{ min: 0 }}
//                         />
//                     </>
//                 )}

//                 {showLimit && (
//                     <TextField
//                         label="Limit uczestników"
//                         name="MaxPlayers"
//                         value={form.MaxPlayers ?? ""}
//                         onChange={handleChange}
//                         type="number"
//                         inputProps={{ min: 0 }}
//                     />
//                 )}

//                 {showScenario && (
//                     <TextField
//                         select
//                         label="Scenariusz"
//                         name="scenario"
//                         value={form.scenario}
//                         onChange={handleChange}
//                     >
//                         {scenarioNames?.map((scenario) => (
//                             <MenuItem
//                                 key={scenario.scenarioName}
//                                 value={scenario.scenarioName}
//                             >
//                                 {scenario.name}
//                             </MenuItem>
//                         ))}
//                     </TextField>
//                 )}

//                 <TextField
//                     type="datetime-local"
//                     label="Start"
//                     InputLabelProps={{ shrink: true }}
//                     value={formatDate(form.startDate)}
//                     onChange={(e) =>
//                         handleDateChange("startDate", e.target.value)
//                     }
//                 />

//                 <TextField
//                     type="datetime-local"
//                     label="Koniec"
//                     InputLabelProps={{ shrink: true }}
//                     value={formatDate(form.endDate)}
//                     onChange={(e) =>
//                         handleDateChange("endDate", e.target.value)
//                     }
//                 />

//             </DialogContent>

//             <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, alignSelf: 'center' }}>
//                 {alert && (
//                     <Alert variant="outlined" severity={alert.severity} style={{ marginTop: 16 }}>
//                         {alert.message}
//                     </Alert>
//                 )}
//             </Box>
//             <DialogActions sx={{ justifyContent: "space-between", alignSelf: "center", mt: 2 }}>
//                 {isEdit && (
//                     <Button color="error" onClick={onDelete}>
//                         Usuń
//                     </Button>
//                 )}
//                 <div>
//                     <Button onClick={onClose}>Anuluj</Button>
//                     <Button variant="contained" onClick={handleSubmit}>
//                         {isEdit ? "Zapisz" : "Dodaj"}
//                     </Button>
//                 </div>
//                 <div style={{ marginTop: "4px" }}>
//                     <div style={{ display: "flex", gap: "8px" }}>
//                         {!event?.meta?.participants?.some(p => p.id === currentUser?.user?.id) && (
//                             <Button
//                                 variant="contained"
//                                 onClick={() => joinEvent(event.id)}
//                                 style={{ backgroundColor: "#4caf50", color: "white" }}
//                             >
//                                 Dołącz
//                             </Button>
//                         )}

//                         {event?.meta?.participants?.some(p => p.id === currentUser?.user?.id) && (
//                             <Button
//                                 variant="contained"
//                                 onClick={() => leaveEvent(event.id)}
//                                 style={{ backgroundColor: "#f44336", color: "white" }}
//                             >
//                                 Opuść
//                             </Button>
//                         )}
//                     </div>
//                 </div>
//             </DialogActions>


//         </Dialog>
//     );
// }

// const formatDate = (date) =>
//     new Date(date).toISOString().slice(0, 16);
